/**
 * DEFCAT P6 — Defect Catalogue LIST page (`/defect-catalogues`).
 *
 * Lists the tenant's catalogues (name, code, default flag, entry count) and
 * offers "New catalogue". Clicking a row opens the editor.
 *
 * Restyled 2026-06-04 (Christian) to the canonical Data-page template
 * (`.vibe/60_TEMPLATE_DATA_PAGE.md`): `<DataPageShell>` / `<DataPageToolbar>`
 * / `<DataTable>` / `<DataPageFooter>` + the `<SearchField>` /
 * `<SingleSelectFilter>` / `<DensityToggleButton>` / `<ColumnVisibilityButton>`
 * filter + settings widgets, matching the Turbines / Parks / Sites list pages.
 *
 * Presentation-only. The list query (`useAdminCataloguesQuery`) is a
 * parameterless `GET /defect-catalogues` that returns ALL of the tenant's
 * catalogues with no server-side search / scope / pagination params — so the
 * Search field, the Default single-select, sorting, and pagination all operate
 * CLIENT-SIDE over the already-fetched array. No query params or query logic
 * changed (the list payload — `DefectCatalogue` — carries no scope/visibility
 * fields, so no scope/visibility filter is offered).
 */
import { useMemo, useState, type ReactElement } from "react";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import type {
  GridColDef,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  SearchField,
  SingleSelectFilter,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import type { DefectCatalogue } from "@aeriosiq/types";
import { useAdminCataloguesQuery } from "./taxonomyQueries.js";
import { NewCatalogueDialog } from "./NewCatalogueDialog.js";
import { useCatalogueWritePermissions } from "./useCatalogueWritePermissions.js";
import { locText } from "./locText.js";
import { PlusIcon } from "@aeriosiq/ui/icons";

const COLUMNS_STORAGE_KEY = "aero.defect-catalogues.columns";

const CATALOGUE_COLUMNS_META: ColumnDescriptor[] = [
  { field: "name", headerName: "Name" },
  { field: "tenantCatalogueCode", headerName: "Code" },
  { field: "isSystemDefault", headerName: "Default" },
  { field: "entryCount", headerName: "Entries" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type DefaultFilter = "all" | "default" | "custom";

/** Row id the DataGrid keys on — the catalogue's stable UUID. */
type CatalogueRow = DefectCatalogue & { id: string };

export function CatalogueListPage(): ReactElement {
  const navigate = useNavigate();
  const cataloguesQuery = useAdminCataloguesQuery();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Client-side filter / sort / pagination state — the list endpoint returns
  // every catalogue in one shot, so all narrowing happens in-memory. Kept in
  // component state (not URL params) to stay strictly presentation-only.
  const [search, setSearch] = useState("");
  const [defaultFilter, setDefaultFilter] = useState<DefaultFilter>("all");
  const [density, setDensity] = useState<Density>("compact");
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  function handleColumnVisibilityChange(next: Record<string, boolean>): void {
    setColumnVisibility(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage unavailable; in-memory state still works.
      }
    }
  }

  // DEFCAT P6 — anyone may READ the list; only edit-roles may create. A
  // tenant-scoped create needs tenant_admin/expert_analyst; a platform-scoped
  // one needs platform_admin (the dialog hides the Platform option unless so).
  const { canEditDraft, canCreatePlatform } =
    useCatalogueWritePermissions(undefined);
  const canCreate = canEditDraft || canCreatePlatform;

  const items: DefectCatalogue[] = useMemo(
    () => cataloguesQuery.data?.items ?? [],
    [cataloguesQuery.data],
  );

  // Client-side narrowing: substring on name + code, plus the Default
  // single-select. Names live in i18n maps so we resolve via `locText`.
  const filtered = useMemo<CatalogueRow[]>(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((cat) => {
        if (defaultFilter === "default" && !cat.isSystemDefault) return false;
        if (defaultFilter === "custom" && cat.isSystemDefault) return false;
        if (q.length === 0) return true;
        const haystack =
          `${locText(cat.name)} ${cat.tenantCatalogueCode}`.toLowerCase();
        return haystack.includes(q);
      })
      .map((cat) => ({ ...cat, id: cat.catalogueId }));
  }, [items, search, defaultFilter]);

  const total = filtered.length;

  const columns = useMemo<GridColDef<CatalogueRow>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        flex: 1,
        minWidth: 220,
        // Sort by the resolved display name, not the raw i18n object.
        valueGetter: (_v, row) => locText(row.name),
        renderCell: (p) => (
          <Typography variant="body2" component="span">
            {locText(p.row.name)}
          </Typography>
        ),
      },
      {
        field: "tenantCatalogueCode",
        headerName: "Code",
        sortable: true,
        width: 180,
        renderCell: (p) =>
          p.row.tenantCatalogueCode ? (
            <Typography
              variant="body2"
              component="span"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {p.row.tenantCatalogueCode}
            </Typography>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "isSystemDefault",
        headerName: "Default",
        sortable: true,
        width: 160,
        valueGetter: (_v, row) => (row.isSystemDefault ? 1 : 0),
        renderCell: (p) =>
          p.row.isSystemDefault ? (
            <Chip label="System default" size="small" />
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "entryCount",
        headerName: "Entries",
        sortable: true,
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (p) => (
          <Box
            sx={{
              width: "100%",
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <Typography
              component="span"
              variant="body2"
              color={p.row.entryCount === 0 ? "text.secondary" : "text.primary"}
            >
              {p.row.entryCount}
            </Typography>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <DataPageShell title="Defect Catalogues">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Defect catalogues toolbar"
        filtersTestId="defect-catalogues-toolbar-filters"
        settingsTestId="defect-catalogues-toolbar-settings"
        filters={
          <>
            <SearchField
              value={search}
              onChange={(next) => {
                setSearch(next);
                setPage(1);
              }}
              placeholder="Search"
              ariaLabel="Search catalogues"
            />
            <SingleSelectFilter
              label="Default"
              width={160}
              value={defaultFilter}
              options={[
                { value: "all", label: "All", display: "" },
                { value: "default", label: "System default" },
                { value: "custom", label: "Custom" },
              ]}
              onChange={(v) => {
                setDefaultFilter(v as DefaultFilter);
                setPage(1);
              }}
            />
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={CATALOGUE_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canCreate ? (
              <Button
                variant="contained"
                aria-label="New catalogue"
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                New catalogue
              </Button>
            ) : null}
          </>
        }
      />

      {cataloguesQuery.isLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress aria-label="Loading catalogues" />
        </Box>
      ) : cataloguesQuery.isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load catalogues.
        </Typography>
      ) : items.length === 0 ? (
        <Box sx={{ flex: 1, py: 4 }}>
          <Typography color="text.secondary">
            No catalogues yet. Create one to get started.
          </Typography>
        </Box>
      ) : (
        <DataTable<CatalogueRow>
          rows={filtered}
          columns={columns}
          ariaLabel="Defect catalogues"
          density={density}
          getRowId={(row) => row.id}
          // Client-side data: the grid sorts + paginates the in-memory
          // filtered array itself (the list endpoint returns everything).
          paginationMode="client"
          sortingMode="client"
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
              setPage(1);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          onRowClick={(p: GridRowParams<CatalogueRow>) => {
            void navigate(
              `/defect-catalogues/${encodeURIComponent(p.row.catalogueId)}`,
            );
          }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="catalogues"
        entityLabelSingular="catalogue"
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        totalTestId="defect-catalogues-footer-total"
      />

      <NewCatalogueDialog
        open={dialogOpen}
        canCreatePlatform={canCreatePlatform}
        onClose={() => {
          setDialogOpen(false);
        }}
        onCreated={(created) => {
          setDialogOpen(false);
          void navigate(
            `/admin/defect-catalogues/${encodeURIComponent(created.catalogueId)}`,
          );
        }}
      />
    </DataPageShell>
  );
}
